/**
 * Phase 8 swipe-action geometry (docs/ops-protocol.md, "Swipe actions"),
 * DOM-free so it can be unit-tested: how far a row's content may slide, and
 * where it settles when the pointer lifts.
 *
 * Offsets are the content's horizontal translation in points: negative
 * reveals the trailing actions (on the right), positive the leading ones.
 */

/** Width of one swipe action button. */
export const SWIPE_ACTION_WIDTH = 74;
/** Pulling this far past the actions' total width performs the first action. */
export const SWIPE_FULL_EXTRA = 60;
/** The content must travel this far before a pan counts as a swipe. */
export const SWIPE_START_DISTANCE = 8;
/** A release faster than this (points per second) opens or closes in the direction of travel. */
export const SWIPE_FLING_VELOCITY = 300;

export type SwipeEdge = 'leading' | 'trailing';

export interface SwipeEdgeSpec {
  /** Number of actions on this edge (0: nothing to reveal). */
  count: number;
  /** Whether pulling past the actions' width + 60 performs the first one. */
  fullSwipe: boolean;
}

export interface SwipeSpec {
  trailing: SwipeEdgeSpec;
  leading: SwipeEdgeSpec;
}

/** Total width of an edge's action buttons. */
export function swipeActionsWidth(count: number): number {
  return Math.max(0, count) * SWIPE_ACTION_WIDTH;
}

/** The edge a content offset reveals (`null` at rest). */
export function swipeEdgeOf(offset: number): SwipeEdge | null {
  if (offset < 0) return 'trailing';
  if (offset > 0) return 'leading';
  return null;
}

/**
 * Clamp a raw pan offset to what the row can show: nothing toward an edge
 * without actions; otherwise up to the actions' width, then with rubber-band
 * resistance (a full swipe is still reachable) up to the row's width.
 */
export function clampSwipeOffset(raw: number, spec: SwipeSpec, rowWidth: number): number {
  const edge = swipeEdgeOf(raw);
  if (!edge) return 0;
  const count = spec[edge].count;
  if (count === 0) return 0;
  const sign = edge === 'trailing' ? -1 : 1;
  const distance = Math.abs(raw);
  const total = swipeActionsWidth(count);
  if (distance <= total) return sign * distance;
  // Past the buttons the content keeps following, slowed down, up to the row width.
  const extra = distance - total;
  const slowed = spec[edge].fullSwipe ? extra * 0.85 : extra * 0.35;
  return sign * Math.min(rowWidth, total + slowed);
}

export type SwipeSettle = 'closed' | 'open' | 'full';

/**
 * Where the content goes when the pointer lifts at `offset` with `velocity`
 * (points per second, same sign convention): `full` performs the edge's
 * first action, `open` parks at the actions' width, `closed` returns to 0.
 */
export function settleSwipe(offset: number, velocity: number, spec: SwipeSpec): SwipeSettle {
  const edge = swipeEdgeOf(offset);
  if (!edge) return 'closed';
  const { count, fullSwipe } = spec[edge];
  if (count === 0) return 'closed';
  const total = swipeActionsWidth(count);
  const distance = Math.abs(offset);
  const sign = edge === 'trailing' ? -1 : 1;
  // Velocity in the revealing direction is positive.
  const along = velocity * sign;
  if (fullSwipe && distance > total + SWIPE_FULL_EXTRA) return 'full';
  if (along > SWIPE_FLING_VELOCITY) return 'open';
  if (along < -SWIPE_FLING_VELOCITY) return 'closed';
  return distance > total / 2 ? 'open' : 'closed';
}

/** The resting offset of a settled row. */
export function swipeRestOffset(settle: SwipeSettle, edge: SwipeEdge, spec: SwipeSpec): number {
  if (settle === 'closed') return 0;
  const sign = edge === 'trailing' ? -1 : 1;
  return sign * swipeActionsWidth(spec[edge].count);
}

/**
 * Velocity estimate from pointer samples: the displacement over the samples
 * of the last `windowMs` (at least the last two), in points per second.
 */
export function velocityFrom(samples: readonly { t: number; x: number; y: number }[], windowMs = 100): { x: number; y: number } {
  if (samples.length < 2) return { x: 0, y: 0 };
  const last = samples[samples.length - 1]!;
  let first = samples[samples.length - 2]!;
  for (let i = samples.length - 2; i >= 0; i--) {
    const s = samples[i]!;
    if (last.t - s.t > windowMs) break;
    first = s;
  }
  const dt = Math.max(1, last.t - first.t);
  return { x: ((last.x - first.x) / dt) * 1000, y: ((last.y - first.y) / dt) * 1000 };
}
